import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, RefreshCw } from 'lucide-react';
import {
  api,
  ApiError,
  type AdminOverview,
  type EnvOverrides,
  type ImageFormat,
  type PushUserState,
} from '../api/client.ts';
import { formatFileSize, formatLastSeen, formatMoney } from '../lib/format.ts';
import { AdminCalls } from './AdminCalls.tsx';
import { AdminGallery } from './AdminGallery.tsx';
import { AdminBots } from './AdminBots.tsx';
import { AdminIntegrations } from './AdminIntegrations.tsx';
import './AdminScreen.css';

const REFRESH_MS = 3000;

/**
 * Neither of the other two tabs belongs at the bottom of this page's long
 * scroll: nothing else on it is about who outside the app may write into a
 * chat, and nothing else is about a phone quietly copying its camera roll here.
 *
 * The split into per-tab files has started rather than been decided against —
 * both of those live in their own file already, and what is left in this one is
 * the overview and its 1600 lines. They also load their own data, which matters
 * more than tidiness: this component polls every three seconds, and neither a
 * list of integrations nor a walk of the disk should be on a timer.
 */
type AdminTab = 'overview' | 'calls' | 'gallery' | 'bots' | 'integrations';

const TABS: { id: AdminTab; label: string }[] = [
  { id: 'overview', label: 'Обзор' },
  { id: 'calls', label: 'Звонки' },
  { id: 'gallery', label: 'Галерея' },
  { id: 'bots', label: 'Боты' },
  { id: 'integrations', label: 'Интеграции' },
];

/**
 * A foreground claim suppresses a push only for the conversation it names, and
 * it expires after 30s server-side. Both halves have to be on screen: an age
 * alone would make an expired claim look like an active one, and an active
 * claim with no conversation behind it silences nothing at all.
 */
function foregroundState(user: PushUserState): { label: string; tone: Tone } {
  const freshest = user.foregroundClaimAges[0];
  if (freshest === undefined) return { label: 'нет', tone: 'neutral' };
  if (freshest >= 30) return { label: `истекла (${freshest} с)`, tone: 'neutral' };

  const chat = user.watching.find((id) => id !== null);
  return chat === undefined
    ? { label: `в приложении, ${freshest} с назад`, tone: 'neutral' }
    : { label: `чат ${chat.slice(0, 8)}, ${freshest} с назад`, tone: 'warn' };
}

type Tone = 'good' | 'warn' | 'bad' | 'neutral';

/**
 * Status never rides on colour alone — each pill carries its own words, so it
 * survives a colourblind reader, a greyscale print and forced-colours mode.
 */
function Pill({ tone, children }: { tone: Tone; children: React.ReactNode }) {
  return <span className={`pill pill-${tone}`}>{children}</span>;
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {hint && <div className="stat-hint">{hint}</div>}
    </div>
  );
}

function formatAge(seconds: number): string {
  if (seconds < 60) return `${seconds} с`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} мин`;
  return `${Math.round(seconds / 3600)} ч`;
}

/**
 * Every push acknowledged is the healthy case. Anything unacknowledged means it
 * left the server and no device ever confirmed it — on Android that is Doze, and
 * it is exactly what this column exists to make visible.
 */
function deliveryTone(delivery: PushUserState['delivery']): Tone {
  if (delivery.sent === 0) return 'neutral';
  if (delivery.acked === delivery.sent) return 'good';
  return delivery.acked === 0 ? 'bad' : 'warn';
}

/**
 * A push the service refused never reached a device to be acknowledged, so it
 * cannot show up in the column beside it however long you wait. It is always
 * bad — unlike an unacknowledged send, which is usually just a sleeping phone.
 */
function failureTone(failed: number): Tone {
  return failed === 0 ? 'neutral' : 'bad';
}

function outcomeTone(outcome: string): Tone {
  if (outcome === 'sent') return 'good';
  if (outcome === 'recheck') return 'warn';
  if (outcome === 'disabled' || outcome === 'no-subscription') return 'bad';
  return 'neutral';
}

/** The outcomes, in Russian, so the log reads as an explanation rather than a code. */
const OUTCOME_LABEL: Record<string, string> = {
  sent: 'отправлено',
  'skipped-active': 'пропущено — этот чат открыт',
  'no-subscription': 'нет подписки',
  disabled: 'push выключен',
  recheck: 'повторная проверка',
};

/** The kinds of failure a device reports, in the words the page shows them in. */
const ERROR_KIND_LABEL: Record<string, string> = {
  api: 'запросы',
  upload: 'отправка файлов',
  socket: 'связь',
  push: 'уведомления',
  crash: 'сбои',
  lifecycle: 'перезапуски',
  call: 'звонки',
};

/** `запросы 12 · связь 3` — the breakdown as one line in a cell. */
function kindSummary(kinds: [string, number][]): string {
  if (kinds.length === 0) return '—';
  return kinds.map(([kind, count]) => `${ERROR_KIND_LABEL[kind] ?? kind} ${count}`).join(' · ');
}

/**
 * Anything in the last day is worth looking at now; older is history. The
 * table is sorted by the total, so the tone is what separates a phone that had
 * a bad afternoon last week from one that is failing while you read this.
 */
function errorTone(recent: number): Tone {
  return recent === 0 ? 'neutral' : recent < 10 ? 'warn' : 'bad';
}

/** The compression knobs, in the units the form shows. */
interface ImageDraft {
  quality: string;
  maxWidth: string;
  maxHeight: string;
  format: ImageFormat;
  maxUploadMb: string;
}

function imageDraftFrom(images: AdminOverview['images']): ImageDraft {
  return {
    quality: String(images.quality),
    maxWidth: String(images.maxWidth),
    maxHeight: String(images.maxHeight),
    format: images.format,
    maxUploadMb: String(images.maxUploadMb),
  };
}

/**
 * The recording knobs, as strings, because they come out of number inputs —
 * except the flip, which comes out of a checkbox and is the one thing here that
 * is a feature rather than a quality.
 */
interface RecordingDraft {
  audioKbPerMinute: string;
  audioGain: string;
  videoPx: string;
  videoFps: string;
  videoKbps: string;
  videoFlip: boolean;
}

/** The transcription knobs. The seconds are a string for the same reason. */
interface SpeechDraft {
  transcribeAuto: boolean;
  transcribeMaxSeconds: string;
}

function speechDraftFrom(transcription: AdminOverview['transcription']): SpeechDraft {
  return {
    transcribeAuto: transcription.transcribeAuto,
    transcribeMaxSeconds: String(transcription.transcribeMaxSeconds),
  };
}

/** The assistant's knobs. Numbers as strings for the number-input reason above. */
interface AiDraft {
  aiReplies: boolean;
  aiHistoryMessages: string;
  aiTextRpd: string;
  aiImageRpd: string;
  aiUserTextPerDay: string;
  aiUserImagePerDay: string;
  /** In dollars in the field, in cents on the wire — see saveAiSettings. */
  aiMonthlyBudget: string;
  aiImageSize: string;
  aiImageQuality: string;
  systemPrompt: string;
}

function aiDraftFrom(ai: AdminOverview['ai']): AiDraft {
  return {
    aiReplies: ai.aiReplies,
    aiHistoryMessages: String(ai.aiHistoryMessages),
    aiTextRpd: String(ai.aiTextRpd),
    aiImageRpd: String(ai.aiImageRpd),
    aiUserTextPerDay: String(ai.aiUserTextPerDay),
    aiUserImagePerDay: String(ai.aiUserImagePerDay),
    aiMonthlyBudget: (ai.aiMonthlyBudgetCents / 100).toFixed(2),
    aiImageSize: ai.aiImageSize,
    aiImageQuality: ai.aiImageQuality,
    systemPrompt: ai.systemPrompt,
  };
}

function recordingDraftFrom(recording: AdminOverview['recording']): RecordingDraft {
  return {
    audioKbPerMinute: String(recording.audioKbPerMinute),
    audioGain: String(recording.audioGain),
    videoPx: String(recording.videoPx),
    videoFps: String(recording.videoFps),
    videoKbps: String(recording.videoKbps),
    videoFlip: recording.videoFlip,
  };
}

/** The same, for what a picked video is re-encoded to. */
interface VideoImportDraft {
  importPx: string;
  importFps: string;
  importKbps: string;
  importAudioKbps: string;
}

function videoImportDraftFrom(videoImport: AdminOverview['videoImport']): VideoImportDraft {
  return {
    importPx: String(videoImport.importPx),
    importFps: String(videoImport.importFps),
    importKbps: String(videoImport.importKbps),
    importAudioKbps: String(videoImport.importAudioKbps),
  };
}

/** The «Новый аккаунт» form. Mirrors what the seed script takes on the host. */
interface AccountDraft {
  username: string;
  password: string;
  isAdmin: boolean;
}

const EMPTY_ACCOUNT: AccountDraft = { username: '', password: '', isAdmin: false };

const FORMAT_LABEL: Record<ImageFormat, string> = {
  webp: 'WebP — самый маленький при том же качестве',
  jpeg: 'JPEG — читается вообще всем',
  original: 'Как прислали — только уменьшение и сжатие',
};

export function AdminScreen() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<AdminTab>('overview');
  const [data, setData] = useState<AdminOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [imageDraft, setImageDraft] = useState<ImageDraft | null>(null);
  const [recordingDraft, setRecordingDraft] = useState<RecordingDraft | null>(null);
  const [videoImportDraft, setVideoImportDraft] = useState<VideoImportDraft | null>(null);
  const [speechDraft, setSpeechDraft] = useState<SpeechDraft | null>(null);
  const [aiDraft, setAiDraft] = useState<AiDraft | null>(null);
  const [account, setAccount] = useState<AccountDraft>(EMPTY_ACCOUNT);
  /** Off by default: the person at the keyboard is not always alone at the desk. */
  const [showPassword, setShowPassword] = useState(false);
  const [accountNotice, setAccountNotice] = useState<string | null>(null);
  /**
   * The nickname a «занят» came back for. Held as the nickname rather than a
   * flag, so the offer to take that account over disappears the moment the field
   * is edited into a different one.
   */
  const [conflict, setConflict] = useState<string | null>(null);
  /** The .env block, once it has been asked for. `null` means it is hidden. */
  const [envText, setEnvText] = useState<EnvOverrides | null>(null);
  const [copied, setCopied] = useState(false);
  /** The rendered block, so a failed clipboard write can select it instead. */
  const envBlock = useRef<HTMLPreElement>(null);

  const load = useCallback(async () => {
    try {
      const next = await api.adminOverview();
      setData(next);
      // Only seed the form on first load: the page polls every few seconds, and
      // overwriting the inputs mid-edit would fight whoever is typing.
      setImageDraft((current) => current ?? imageDraftFrom(next.images));
      setRecordingDraft((current) => current ?? recordingDraftFrom(next.recording));
      setVideoImportDraft((current) => current ?? videoImportDraftFrom(next.videoImport));
      setSpeechDraft((current) => current ?? speechDraftFrom(next.transcription));
      setAiDraft((current) => current ?? aiDraftFrom(next.ai));
      setError(null);
    } catch {
      setError('Не удалось загрузить данные');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * «Разобрано». No optimistic removal — the three-second poll takes the row off
   * the list on its own, and a list that emptied itself before the server agreed
   * would be the wrong thing to trust on the one page whose whole job is saying
   * what is actually true.
   */
  const resolveReport = useCallback(
    async (id: string) => {
      try {
        await api.resolveReport(id);
        await load();
      } catch {
        setError('Не удалось отметить жалобу');
      }
    },
    [load],
  );

  // Polled rather than pushed: the point of this screen is to watch a foreground
  // claim age out while you background a phone, which needs it to move on its own.
  //
  // Only while its own tab is on screen, though. Nothing outside «Обзор» reads
  // this payload, and the other two tabs are where somebody stands still for
  // minutes at a time — reading a sync journal, comparing two accounts on the
  // chart — which was twenty requests a minute counting sessions and walking
  // the uploads directory for a section nobody was looking at. Separate from
  // the first load above rather than one effect with `tab` in its dependencies,
  // so coming back to this tab restarts the clock instead of refetching on
  // every press of every tab.
  useEffect(() => {
    if (tab !== 'overview') return;
    const timer = window.setInterval(() => void load(), REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load, tab]);

  async function act(run: () => Promise<unknown>, done: string) {
    setBusy(true);
    setNotice(null);
    try {
      await run();
      setNotice(done);
      await load();
    } catch {
      setNotice('Не удалось выполнить');
    }
    setBusy(false);
  }

  async function saveImageSettings() {
    if (!imageDraft) return;
    setBusy(true);
    setNotice(null);
    try {
      const next = await api.saveImageSettings({
        quality: Number(imageDraft.quality),
        maxWidth: Number(imageDraft.maxWidth),
        maxHeight: Number(imageDraft.maxHeight),
        format: imageDraft.format,
        maxUploadMb: Number(imageDraft.maxUploadMb),
      });
      // Echoed back after clamping, so an out-of-range entry corrects itself.
      setImageDraft(imageDraftFrom(next));
      setNotice('Сохранено — применится к следующей загрузке');
      await load();
    } catch {
      setNotice('Не удалось сохранить');
    }
    setBusy(false);
  }

  async function resetImageSettings() {
    setBusy(true);
    setNotice(null);
    try {
      const next = await api.resetImageSettings();
      setImageDraft(imageDraftFrom(next));
      setNotice('Сброшено к значениям из .env');
      await load();
    } catch {
      setNotice('Не удалось сбросить');
    }
    setBusy(false);
  }

  async function saveSpeechSettings() {
    if (!speechDraft) return;
    setBusy(true);
    setNotice(null);
    try {
      const next = await api.saveTranscriptionSettings({
        transcribeAuto: speechDraft.transcribeAuto,
        transcribeMaxSeconds: Number(speechDraft.transcribeMaxSeconds),
      });
      // Echoed back after clamping, so an out-of-range entry corrects itself.
      setSpeechDraft(speechDraftFrom(next));
      setNotice('Сохранено — действует со следующего голосового сообщения');
      await load();
    } catch {
      setNotice('Не удалось сохранить');
    }
    setBusy(false);
  }

  async function resetSpeechSettings() {
    setBusy(true);
    setNotice(null);
    try {
      const next = await api.resetTranscriptionSettings();
      setSpeechDraft(speechDraftFrom(next));
      setNotice('Сброшено к значениям из .env');
      await load();
    } catch {
      setNotice('Не удалось сбросить');
    }
    setBusy(false);
  }

  async function saveAiSettings() {
    if (!aiDraft) return;
    setBusy(true);
    setNotice(null);
    try {
      const next = await api.saveAiSettings({
        aiReplies: aiDraft.aiReplies,
        aiHistoryMessages: Number(aiDraft.aiHistoryMessages),
        aiTextRpd: Number(aiDraft.aiTextRpd),
        aiImageRpd: Number(aiDraft.aiImageRpd),
        aiUserTextPerDay: Number(aiDraft.aiUserTextPerDay),
        aiUserImagePerDay: Number(aiDraft.aiUserImagePerDay),
        // Dollars in the field, cents on the wire — the same conversion the
        // image panel does for megabytes, so neither side handles the other's
        // unit and a budget of «двадцать с половиной» stays an integer.
        aiMonthlyBudgetCents: Math.round(Number(aiDraft.aiMonthlyBudget) * 100),
        aiImageSize: aiDraft.aiImageSize,
        aiImageQuality: aiDraft.aiImageQuality,
        // An emptied field hands the persona back to .env — same `null` clears
        // an override convention as every number on this page.
        systemPrompt: aiDraft.systemPrompt.trim() ? aiDraft.systemPrompt : null,
      });
      // Echoed back after clamping, so an out-of-range entry corrects itself.
      setAiDraft(aiDraftFrom(next));
      setNotice('Сохранено — действует со следующего сообщения ассистенту');
      await load();
    } catch {
      setNotice('Не удалось сохранить');
    }
    setBusy(false);
  }

  async function resetAiSettings() {
    setBusy(true);
    setNotice(null);
    try {
      const next = await api.resetAiSettings();
      setAiDraft(aiDraftFrom(next));
      setNotice('Сброшено к значениям из .env');
      await load();
    } catch {
      setNotice('Не удалось сбросить');
    }
    setBusy(false);
  }

  async function saveRecordingSettings() {
    if (!recordingDraft) return;
    setBusy(true);
    setNotice(null);
    try {
      const next = await api.saveRecordingSettings({
        audioKbPerMinute: Number(recordingDraft.audioKbPerMinute),
        audioGain: Number(recordingDraft.audioGain),
        videoPx: Number(recordingDraft.videoPx),
        videoFps: Number(recordingDraft.videoFps),
        videoKbps: Number(recordingDraft.videoKbps),
        videoFlip: recordingDraft.videoFlip,
      });
      // Echoed back after clamping, so an out-of-range entry corrects itself.
      setRecordingDraft(recordingDraftFrom(next));
      setNotice('Сохранено — применится к следующей записи после перезагрузки приложения');
      await load();
    } catch {
      setNotice('Не удалось сохранить');
    }
    setBusy(false);
  }

  async function resetRecordingSettings() {
    setBusy(true);
    setNotice(null);
    try {
      const next = await api.resetRecordingSettings();
      setRecordingDraft(recordingDraftFrom(next));
      setNotice('Сброшено к значениям из .env');
      await load();
    } catch {
      setNotice('Не удалось сбросить');
    }
    setBusy(false);
  }

  async function saveVideoImportSettings() {
    if (!videoImportDraft) return;
    setBusy(true);
    setNotice(null);
    try {
      const next = await api.saveVideoImportSettings({
        importPx: Number(videoImportDraft.importPx),
        importFps: Number(videoImportDraft.importFps),
        importKbps: Number(videoImportDraft.importKbps),
        importAudioKbps: Number(videoImportDraft.importAudioKbps),
      });
      // Echoed back after clamping, so an out-of-range entry corrects itself.
      setVideoImportDraft(videoImportDraftFrom(next));
      setNotice('Сохранено — применится к следующему видео после перезагрузки приложения');
      await load();
    } catch {
      setNotice('Не удалось сохранить');
    }
    setBusy(false);
  }

  async function resetVideoImportSettings() {
    setBusy(true);
    setNotice(null);
    try {
      const next = await api.resetVideoImportSettings();
      setVideoImportDraft(videoImportDraftFrom(next));
      setNotice('Сброшено к значениям из .env');
      await load();
    } catch {
      setNotice('Не удалось сбросить');
    }
    setBusy(false);
  }


  /**
   * Creates the account, or — on the second press, once the page has said whose
   * account it is — takes over a nickname that already exists.
   *
   * The first attempt never overwrites. A nickname typed here is one somebody
   * dictated over the phone, and silently resetting the password of whoever
   * already holds it would lock a real person out of a chat they are in the
   * middle of, with nothing on screen to say it happened.
   */
  async function createAccount(overwrite = false) {
    setBusy(true);
    setAccountNotice(null);
    try {
      const result = await api.createUser({ ...account, overwrite });
      setAccount(EMPTY_ACCOUNT);
      setShowPassword(false);
      setConflict(null);
      setAccountNotice(
        result.created
          ? `Аккаунт «${result.account.username}» создан — пароль передайте сами, здесь его больше не увидеть`
          : `Пароль «${result.account.username}» изменён, сессии отозваны`,
      );
      await load();
    } catch (error) {
      // 409 is the one failure with a way forward, so it is the only one that
      // puts the take-over button on screen; see PROVISION_ERROR on the server.
      const taken = error instanceof ApiError && error.status === 409;
      setConflict(taken ? account.username.trim() : null);
      setAccountNotice(error instanceof ApiError ? error.message : 'Не удалось создать аккаунт');
    }
    setBusy(false);
  }

  /**
   * Asked for on demand rather than polled: it is a snapshot to copy, and a
   * block of text that rewrites itself every three seconds under the cursor is
   * the one thing it must not be. A second press hides it again.
   */
  async function toggleEnv() {
    if (envText) {
      setEnvText(null);
      return;
    }
    setBusy(true);
    setNotice(null);
    try {
      setEnvText(await api.envOverrides());
      setCopied(false);
    } catch {
      setNotice('Не удалось собрать .env');
    }
    setBusy(false);
  }

  async function copyEnv() {
    if (!envText) return;
    try {
      await navigator.clipboard.writeText(envText.text);
      setCopied(true);
    } catch {
      // An insecure origin, or a browser that will not write to the clipboard
      // without a permission nobody granted. Selecting the block leaves the
      // person one Ctrl+C away rather than one apology away.
      const node = envBlock.current;
      if (node) {
        const range = document.createRange();
        range.selectNodeContents(node);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
      setNotice('Буфер обмена недоступен — текст выделен, скопируйте вручную');
    }
  }

  if (error && !data) return <div className="admin-screen admin-error">{error}</div>;
  if (
    !data ||
    !imageDraft ||
    !recordingDraft ||
    !videoImportDraft ||
    !speechDraft ||
    !aiDraft
  ) {
    return <div className="admin-screen" />;
  }

  const {
    totals,
    accounts,
    push,
    clientErrors,
    reports,
    images,
    recording,
    videoImport,
    transcription,
    ai,
  } = data;
  /** Whether the nickname in the field is the one the server said was taken. */
  const conflicted = conflict !== null && conflict === account.username.trim();
  /** Everything that put bytes in uploads/, which is what `disk` counts. An
      album is one message and up to ten of those files, which is the largest
      single source of the gap the note below explains. */
  const withAttachment =
    totals.images + totals.files + totals.videoNotes + totals.voiceMessages + totals.albums;
  const imagesTuned = images.overridden.length > 0;
  const recordingTuned = recording.overridden.length > 0;
  const videoImportTuned = videoImport.overridden.length > 0;
  const speechTuned = transcription.overridden.length > 0;
  const aiTuned = ai.overridden.length > 0 || ai.systemPromptOverridden;
  const imageField = (key: Exclude<keyof ImageDraft, 'format'>) => ({
    value: imageDraft[key],
    onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
      setImageDraft({ ...imageDraft, [key]: event.target.value }),
  });
  const recordingField = (key: Exclude<keyof RecordingDraft, 'videoFlip'>) => ({
    value: recordingDraft[key],
    onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
      setRecordingDraft({ ...recordingDraft, [key]: event.target.value }),
  });
  const videoImportField = (key: keyof VideoImportDraft) => ({
    value: videoImportDraft[key],
    onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
      setVideoImportDraft({ ...videoImportDraft, [key]: event.target.value }),
  });
  return (
    <div className="admin-screen">
      <header className="admin-header">
        <button type="button" className="icon-button admin-back" onClick={() => navigate('/')}>
          <ChevronLeft size={18} />
          <span>Назад</span>
        </button>
        <h1 className="admin-title">Панель администратора</h1>
        <button
          type="button"
          className="icon-button admin-refresh"
          onClick={() => void load()}
          aria-label="Обновить"
        >
          <RefreshCw size={16} />
        </button>
      </header>

      {/* Tabs, not more sections: neither what outside services may write into
          a chat nor what a phone has copied here is a counter to watch. */}
      <nav className="admin-tabs" role="tablist" aria-label="Разделы панели">
        {TABS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            role="tab"
            aria-selected={tab === entry.id}
            className={tab === entry.id ? 'admin-tab admin-tab-active' : 'admin-tab'}
            onClick={() => setTab(entry.id)}
          >
            {entry.label}
          </button>
        ))}
      </nav>

      {tab === 'calls' && <AdminCalls />}

      {tab === 'gallery' && <AdminGallery />}

      {tab === 'bots' && <AdminBots />}

      {tab === 'integrations' && <AdminIntegrations />}

      {tab === 'overview' && (
        <>
          <section className="admin-section">
            <h2 className="admin-section-title">Всего</h2>
            <div className="stat-grid">
              <Stat label="Сообщений" value={String(totals.messages)} hint={`${totals.unread} непрочитанных`} />
              <Stat label="Текстовых" value={String(totals.textMessages)} />
              <Stat label="Изображений" value={String(totals.images)} />
              <Stat label="Групп фото" value={String(totals.albums)} />
              <Stat label="Файлов" value={String(totals.files)} />
              <Stat label="Кружочков" value={String(totals.videoNotes)} />
              <Stat
                label="Голосовых"
                value={String(totals.voiceMessages)}
                hint={`${totals.transcripts} расшифровано`}
              />
              <Stat
                label="Вложений на диске"
                value={formatFileSize(totals.disk.bytes)}
                hint={`${totals.disk.files} файлов в uploads/`}
              />
              <Stat label="Пользователей" value={String(totals.users)} hint={`${totals.conversations} чатов`} />
              <Stat label="Активных сессий" value={String(totals.sessions)} />
            </div>
            {totals.disk.files !== withAttachment && (
              <p className="admin-note">
                На диске {totals.disk.files} файлов, а сообщений с вложениями {withAttachment}.
                Расхождение — это превью (у изображений и кружочков их по два файла),
                осиротевшие файлы или удалённые сообщения.
              </p>
            )}
          </section>

          {/* The one part of this page the server did not measure itself. A request
              that never arrived leaves nothing in any log here, so the device keeps
              its own note and hands it over when it can — which is why a total can
              appear minutes or a day after the failure it describes. */}
          <section className="admin-section">
            <h2 className="admin-section-title">Ошибки на устройствах</h2>
            {clientErrors.total === 0 ? (
              <p className="admin-note">
                Пока ничего. Устройства сами сообщают, до чего не смогли достучаться — запросы,
                загрузки, разрывы связи, подписка на уведомления и сбои приложения. Пока связь в
                порядке, здесь пусто.
              </p>
            ) : (
              <>
                <div className="stat-grid">
                  <Stat
                    label="Всего"
                    value={String(clientErrors.total)}
                    hint={kindSummary(clientErrors.kinds)}
                  />
                  <Stat
                    label="За сутки"
                    value={String(clientErrors.recent)}
                    hint={clientErrors.recent === 0 ? 'сейчас тихо' : 'по часам на устройствах'}
                  />
                </div>

                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Пользователь</th>
                        <th>Всего</th>
                        <th>За сутки</th>
                        <th>Последняя</th>
                        <th>Из чего</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientErrors.users.map((user) => (
                        <tr key={user.username}>
                          <td>{user.username}</td>
                          <td>{user.total}</td>
                          <td>
                            <Pill tone={errorTone(user.recent)}>{user.recent}</Pill>
                          </td>
                          <td className="admin-hosts">{formatLastSeen(user.lastAt)}</td>
                          <td className="admin-hosts">{kindSummary(user.kinds)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="admin-note">
                  Здесь только счётчики. Что именно не открылось и что ответил сервер — в таблице{' '}
                  <code>client_errors</code>: <code>kind</code>, <code>detail</code>,{' '}
                  <code>message</code>, <code>status</code> и две отметки времени — когда случилось на
                  устройстве и когда отчёт дошёл сюда. Хранятся последние 20 000 записей.
                </p>
              </>
            )}
          </section>

          {/* The other half of the same idea, and the opposite of it: above is what
              devices noticed about themselves, here is what a person decided was
              worth telling somebody. Rare, deliberate, and read one at a time. */}
          <section className="admin-section">
            <h2 className="admin-section-title">Жалобы</h2>
            {reports.length === 0 ? (
              <p className="admin-note">
                Пусто. Кнопка «Отправить ошибку Олегу =)» стоит на отказах ассистента — когда объяснения в чате
                недостаточно, отчёт приходит сюда вместе с обстоятельствами: провайдер, модель и та
                самая фраза, которую бот сказал вслух.
              </p>
            ) : (
              <ul className="admin-reports">
                {reports.map((report) => (
                  <li key={report.id} className="admin-report">
                    <div className="admin-report-head">
                      <b>{report.userName}</b>
                      <span className="admin-hosts">{formatLastSeen(report.createdAt)}</span>
                      <button
                        type="button"
                        className="settings-secondary"
                        onClick={() => void resolveReport(report.id)}
                      >
                        Разобрано
                      </button>
                    </div>
                    {report.note && <p className="admin-report-note">{report.note}</p>}
                    <pre className="admin-report-context">{report.context}</pre>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* The one thing on this page that is not a knob: until now an account
              could only be made with a shell on the host, which meant the person who
              runs the app could not add anyone without the person who deploys it. */}
          <section className="admin-section">
            <h2 className="admin-section-title">Пользователи</h2>
            <div className="admin-tuning">
              <div className="admin-tuning-head">
                <h3 className="admin-tuning-title">Новый аккаунт</h3>
                <span className="admin-tuning-hint">
                  То же, что <code>seed</code> на сервере: регистрации в приложении нет, аккаунты
                  создаются только так. Войти можно сразу. Пароль нигде больше не показывается —
                  его нельзя посмотреть потом, только задать новый.
                </span>
              </div>

              <div className="admin-fields">
                <label className="admin-field">
                  <span>Никнейм</span>
                  <input
                    type="text"
                    value={account.username}
                    autoComplete="off"
                    autoCapitalize="none"
                    spellCheck={false}
                    onChange={(event) => setAccount({ ...account, username: event.target.value })}
                  />
                  <small>до 32 символов, без пробелов</small>
                </label>
                <label className="admin-field">
                  <span>Пароль</span>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={account.password}
                    autoComplete="new-password"
                    onChange={(event) => setAccount({ ...account, password: event.target.value })}
                  />
                  <small>минимум 8 символов</small>
                </label>
              </div>

              <label className="admin-switch admin-switch-inline">
                <input
                  type="checkbox"
                  checked={account.isAdmin}
                  onChange={(event) => setAccount({ ...account, isAdmin: event.target.checked })}
                />
                <span>
                  Администратор
                  <span className="settings-hint">Доступ к этой странице и ко всем её настройкам.</span>
                </span>
              </label>

              <label className="admin-switch admin-switch-inline">
                <input
                  type="checkbox"
                  checked={showPassword}
                  onChange={(event) => setShowPassword(event.target.checked)}
                />
                <span>
                  Показать пароль
                  <span className="settings-hint">
                    Чтобы продиктовать его без опечатки — если рядом никого нет.
                  </span>
                </span>
              </label>

              <div className="admin-actions">
                <button
                  type="button"
                  className="primary-button admin-save"
                  disabled={busy || !account.username.trim() || account.password.length < 8}
                  onClick={() => void createAccount()}
                >
                  Создать
                </button>
                {conflicted && (
                  <button
                    type="button"
                    className="settings-secondary"
                    disabled={busy}
                    onClick={() => void createAccount(true)}
                  >
                    Задать новый пароль «{conflict}»
                  </button>
                )}
                {accountNotice && <span className="admin-notice">{accountNotice}</span>}
              </div>

              {conflicted && (
                <p className="admin-note">
                  Такой никнейм уже занят. Второй кнопкой можно задать этому аккаунту новый пароль и
                  роль — тогда все его сессии отзываются и человеку придётся войти заново. Если это не
                  тот, кого вы заводите, просто поправьте никнейм.
                </p>
              )}
            </div>

            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Никнейм</th>
                    <th>Роль</th>
                    <th>Создан</th>
                    <th>Сессий</th>
                    <th>Был в сети</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((row) => (
                    <tr key={row.id}>
                      <td>{row.username}</td>
                      <td>
                        {row.isAdmin ? (
                          <Pill tone="warn">администратор</Pill>
                        ) : (
                          <Pill tone="neutral">обычный</Pill>
                        )}
                      </td>
                      <td className="admin-hosts">
                        {new Date(row.createdAt).toLocaleDateString('ru-RU')}
                      </td>
                      <td>{row.sessions}</td>
                      <td className="admin-hosts">
                        {row.lastSeenAt === null ? 'никогда' : formatLastSeen(row.lastSeenAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="admin-note">
              Удалить аккаунт отсюда нельзя — вместе с ним ушли бы все его сообщения у собеседников.
              Свой собственный пароль тоже не поменять: это отозвало бы сессию, из которой вы сейчас
              здесь, — для него остаётся seed-скрипт на сервере.
            </p>
          </section>

          <section className="admin-section">
            <h2 className="admin-section-title">Изображения</h2>
            <div className="admin-tuning">
              <div className="admin-tuning-head">
                <h3 className="admin-tuning-title">Сжатие при загрузке</h3>
                <span className="admin-tuning-hint">
                  Применяется к следующей загрузке, без перезапуска. Уже отправленные снимки не
                  трогаются. Из фотографии убираются метаданные — включая координаты съёмки.
                </span>
              </div>

              <div className="admin-fields">
                <label className="admin-field">
                  <span>Качество, 1–100</span>
                  <input type="number" min={20} max={100} {...imageField('quality')} />
                  <small>из .env: {images.envDefaults.quality}</small>
                </label>
                <label className="admin-field">
                  <span>Макс. ширина, px</span>
                  <input type="number" min={200} max={8000} {...imageField('maxWidth')} />
                  <small>из .env: {images.envDefaults.maxWidth}</small>
                </label>
                <label className="admin-field">
                  <span>Макс. высота, px</span>
                  <input type="number" min={200} max={8000} {...imageField('maxHeight')} />
                  <small>из .env: {images.envDefaults.maxHeight}</small>
                </label>
                <label className="admin-field">
                  <span>Формат</span>
                  <select
                    value={imageDraft.format}
                    onChange={(event) =>
                      setImageDraft({ ...imageDraft, format: event.target.value as ImageFormat })
                    }
                  >
                    {(Object.keys(FORMAT_LABEL) as ImageFormat[]).map((format) => (
                      <option key={format} value={format}>
                        {format}
                      </option>
                    ))}
                  </select>
                  <small>{FORMAT_LABEL[imageDraft.format]}</small>
                </label>
                <label className="admin-field">
                  <span>Макс. размер файла, МБ</span>
                  <input type="number" min={1} max={400} {...imageField('maxUploadMb')} />
                  <small>из .env: {images.envDefaults.maxUploadMb} МБ</small>
                </label>
              </div>

              <div className="admin-actions">
                <button
                  type="button"
                  className="primary-button admin-save"
                  disabled={busy}
                  onClick={() => void saveImageSettings()}
                >
                  Сохранить
                </button>
                <button
                  type="button"
                  className="settings-secondary"
                  disabled={busy || !imagesTuned}
                  onClick={() => void resetImageSettings()}
                >
                  Вернуть значения из .env
                </button>
                {imagesTuned && <Pill tone="warn">переопределено здесь, не из .env</Pill>}
              </div>

              <p className="admin-note">
                Ограничение на размер файла касается любых вложений, не только картинок, и не может
                быть больше лимита прокси (<code>request_body</code> в chat-site.caddy). GIF не
                пережимается — иначе пропадёт анимация. Если после сжатия файл получился больше
                исходного и уменьшать было нечего, остаётся исходный.
              </p>
            </div>
          </section>

          <section className="admin-section">
            <h2 className="admin-section-title">Записи</h2>
            <div className="admin-tuning">
              <div className="admin-tuning-head">
                <h3 className="admin-tuning-title">Качество звука и видео</h3>
                <span className="admin-tuning-hint">
                  Звук — сколько весит минута, одинаково для голосового сообщения и для дорожки
                  кружочка. Видео — только для кружочков: они квадратные, так что это сторона кадра,
                  частота и битрейт. Всё сжимается на телефоне во время записи, поэтому значения
                  приезжают на устройство при входе и действуют со следующей записи после перезагрузки
                  приложения.
                </span>
              </div>

              <div className="admin-fields">
                <label className="admin-field">
                  <span>Звук, КБ на минуту</span>
                  <input type="number" min={400} max={800} step={50} {...recordingField('audioKbPerMinute')} />
                  <small>
                    это ≈{recording.audioKbps} кбит/с · из .env:{' '}
                    {recording.envDefaults.audioKbPerMinute} КБ
                  </small>
                </label>
                <label className="admin-field">
                  <span>Громкость записи, ×</span>
                  <input type="number" min={1} max={6} step={0.1} {...recordingField('audioGain')} />
                  <small>
                    микрофон усиливается перед сжатием, пики срезает лимитер · 1 — как отдаёт
                    браузер · из .env: ×{recording.envDefaults.audioGain}
                  </small>
                </label>
                <label className="admin-field">
                  <span>Сторона кадра, px</span>
                  <input type="number" min={256} max={720} step={16} {...recordingField('videoPx')} />
                  <small>из .env: {recording.envDefaults.videoPx} px</small>
                </label>
                <label className="admin-field">
                  <span>Кадров в секунду</span>
                  <input type="number" min={12} max={30} {...recordingField('videoFps')} />
                  <small>из .env: {recording.envDefaults.videoFps}</small>
                </label>
                <label className="admin-field">
                  <span>Битрейт видео, кбит/с</span>
                  <input type="number" min={200} max={2000} step={50} {...recordingField('videoKbps')} />
                  <small>из .env: {recording.envDefaults.videoKbps} кбит/с</small>
                </label>
                <div className="admin-field admin-field-readout">
                  <span>Минута кружочка</span>
                  <strong>≈{recording.videoMbPerMinute} МБ</strong>
                  <small>
                    видео + звук. Кружочек ограничен 60 секундами, так что это и есть самый большой
                    файл; лимит загрузки — {images.maxUploadMb} МБ
                  </small>
                </div>
              </div>

              <label className="admin-switch">
                <input
                  type="checkbox"
                  checked={recordingDraft.videoFlip}
                  onChange={(event) =>
                    setRecordingDraft({ ...recordingDraft, videoFlip: event.target.checked })
                  }
                />
                <span>
                  Разрешить переключать камеру в кружочке
                  <span className="settings-hint">
                    Браузер не умеет менять камеру у начатой записи, поэтому каждое переключение
                    режет запись на части: они снимаются по очереди и склеиваются в один файл прямо
                    на телефоне, аппаратным кодеком через WebCodecs. Цена — стык примерно в полсекунды
                    на каждом переключении (телефон отпускает одну камеру и открывает другую) и
                    несколько секунд сборки после отпускания кнопки, пока в пилюле идёт «Собираем
                    кружочек %». Выключите, если сборка ведёт себя странно: тогда кнопки смены камеры
                    нет нигде и кружочек всегда снимается на фронталку. Из .env:{' '}
                    {recording.envDefaults.videoFlip ? 'включено' : 'выключено'}.
                  </span>
                </span>
              </label>

              <div className="admin-actions">
                <button
                  type="button"
                  className="primary-button admin-save"
                  disabled={busy}
                  onClick={() => void saveRecordingSettings()}
                >
                  Сохранить
                </button>
                <button
                  type="button"
                  className="settings-secondary"
                  disabled={busy || !recordingTuned}
                  onClick={() => void resetRecordingSettings()}
                >
                  Вернуть значения из .env
                </button>
                {recordingTuned && <Pill tone="warn">переопределено здесь, не из .env</Pill>}
              </div>

              <p className="admin-note">
                Ниже 400 КБ звук становится глухим, выше 800 — разницы уже не слышно, поэтому
                диапазон такой узкий. Вес минуты кружочка почти целиком определяет битрейт видео:
                звук на общую цифру влияет слабо. Сторона кадра 384 px — это ровно тот размер, в
                котором кружочек и показывается, на экране 2× ; больше имеет смысл, только если
                увеличивать сам кружочек. Проверьте, что минута помещается в лимит загрузки.
              </p>
            </div>

            <div className="admin-tuning">
              <div className="admin-tuning-head">
                <h3 className="admin-tuning-title">Видео из галереи</h3>
                <span className="admin-tuning-hint">
                  Ролик, снятый камерой телефона, весит абсурдно много: 24 секунды с Honor — это
                  50 МБ, то есть 16,7 Мбит/с. Перед отправкой он пережимается прямо на телефоне
                  (H.264 + AAC, аппаратный кодек через WebCodecs), и те же 24 секунды уезжают
                  примерно в 7,5 МБ. Пока идёт сжатие, в пузыре написано «Сжимаем %», и только
                  потом начинается «Отправляем %». Ролик, который и так уложился в эти цифры, не
                  трогают — пересжимать его значит терять качество ни за что.
                </span>
              </div>

              <div className="admin-fields">
                <label className="admin-field">
                  <span>Длинная сторона, px</span>
                  <input type="number" min={480} max={1920} step={16} {...videoImportField('importPx')} />
                  <small>
                    короткая подгоняется по пропорции · из .env: {videoImport.envDefaults.importPx} px
                  </small>
                </label>
                <label className="admin-field">
                  <span>Кадров в секунду</span>
                  <input type="number" min={15} max={60} {...videoImportField('importFps')} />
                  <small>
                    только вниз: 24 кадра из файла так и останутся 24 · из .env:{' '}
                    {videoImport.envDefaults.importFps}
                  </small>
                </label>
                <label className="admin-field">
                  <span>Битрейт видео, кбит/с</span>
                  <input
                    type="number"
                    min={500}
                    max={8000}
                    step={100}
                    {...videoImportField('importKbps')}
                  />
                  <small>из .env: {videoImport.envDefaults.importKbps} кбит/с</small>
                </label>
                <label className="admin-field">
                  <span>Битрейт звука, кбит/с</span>
                  <input
                    type="number"
                    min={48}
                    max={192}
                    step={8}
                    {...videoImportField('importAudioKbps')}
                  />
                  <small>из .env: {videoImport.envDefaults.importAudioKbps} кбит/с</small>
                </label>
                <div className="admin-field admin-field-readout">
                  <span>Минута видео</span>
                  <strong>≈{videoImport.mbPerMinute} МБ</strong>
                  <small>
                    видео + звук. У ролика из галереи нет ограничения по длине, так что в лимит
                    загрузки ({images.maxUploadMb} МБ) он упирается сам — это примерно{' '}
                    {Math.floor(images.maxUploadMb / Math.max(videoImport.mbPerMinute, 0.1))} мин
                  </small>
                </div>
              </div>

              <div className="admin-actions">
                <button
                  type="button"
                  className="primary-button admin-save"
                  disabled={busy}
                  onClick={() => void saveVideoImportSettings()}
                >
                  Сохранить
                </button>
                <button
                  type="button"
                  className="settings-secondary"
                  disabled={busy || !videoImportTuned}
                  onClick={() => void resetVideoImportSettings()}
                >
                  Вернуть значения из .env
                </button>
                {videoImportTuned && <Pill tone="warn">переопределено здесь, не из .env</Pill>}
              </div>

              <p className="admin-note">
                Больше всего даёт не битрейт, а размер кадра: плитка альбома — 232 px, во весь
                экран видео открывается в ширину телефона, так что пиксели сверх 720p оплачивает
                мобильный трафик, а выбрасывает декодер. Второе по величине — частота кадров:
                телефоны часто снимают 60, а 30 на съёмке с рук неотличимы. Если браузер не умеет
                WebCodecs (старая Safari), файл уходит как есть — сжатие тут улучшение, а не
                условие отправки.
              </p>
            </div>

            <div className="admin-tuning">
              <div className="admin-tuning-head">
                <h3 className="admin-tuning-title">Распознавание речи</h3>
                <span className="admin-tuning-hint">
                  Голосовые сообщения можно показывать текстом. Распознаёт сервер, а не браузер:
                  так текст получается один на всех и сохраняется в переписке — открыли чат
                  через месяц, расшифровка на месте. Первый, кто нажал «Аа», распознаёт запись
                  для всех участников; повторные нажатия ничего не пересчитывают.
                </span>
              </div>

              {/* The two switches below configure something that will not happen
                  without an engine, so the panel says which it is before it offers
                  them — and never says what the command line or the key are. */}
              <div className="admin-fields">
                <div className="admin-field admin-field-readout">
                  <span>Движок</span>
                  <strong>
                    {transcription.configured
                      ? transcription.provider === 'command'
                        ? 'локальный (TRANSCRIBE_COMMAND)'
                        : 'внешний сервис (TRANSCRIBE_URL)'
                      : 'не настроен'}
                  </strong>
                  <small>
                    {transcription.configured
                      ? 'настраивается только в .env и требует перезапуска'
                      : 'задайте TRANSCRIBE_PROVIDER в .env — пока кнопки «Аа» в чате нет'}
                  </small>
                </div>
                <label className="admin-field">
                  <span>Максимальная длина, секунд</span>
                  <input
                    type="number"
                    min={10}
                    max={3600}
                    step={10}
                    value={speechDraft.transcribeMaxSeconds}
                    onChange={(event) =>
                      setSpeechDraft({ ...speechDraft, transcribeMaxSeconds: event.target.value })
                    }
                  />
                  <small>из .env: {transcription.envDefaults.transcribeMaxSeconds} с</small>
                </label>
              </div>

              <label className="admin-switch">
                <input
                  type="checkbox"
                  checked={speechDraft.transcribeAuto}
                  onChange={(event) =>
                    setSpeechDraft({ ...speechDraft, transcribeAuto: event.target.checked })
                  }
                />
                <span>
                  Распознавать каждое голосовое сразу
                  <span className="settings-hint">
                    Без этого запись распознаётся только по нажатию «Аа» в чате. Включать имеет
                    смысл, если голосовые чаще читают, чем слушают: это процессорное время на
                    каждое сообщение, а очередь на сервере одна и разбирается по одной записи. Из
                    .env: {transcription.envDefaults.transcribeAuto ? 'включено' : 'выключено'}.
                  </span>
                </span>
              </label>

              <div className="admin-actions">
                <button
                  type="button"
                  className="primary-button admin-save"
                  disabled={busy}
                  onClick={() => void saveSpeechSettings()}
                >
                  Сохранить
                </button>
                <button
                  type="button"
                  className="settings-secondary"
                  disabled={busy || !speechTuned}
                  onClick={() => void resetSpeechSettings()}
                >
                  Вернуть значения из .env
                </button>
                {speechTuned && <Pill tone="warn">переопределено здесь, не из .env</Pill>}
              </div>

              <p className="admin-note">
                Распознанное хранится в самом сообщении и не пересчитывается — это и делает
                расшифровку частью истории, а не разовым ответом. Длинная запись держит очередь,
                поэтому у неё есть предел: всё, что длиннее, кнопку «Аа» не примет. Локальный
                движок (whisper.cpp) не выпускает запись с сервера; внешний сервис — отправляет
                ей файл целиком, включая то, что отправитель обрезал в редакторе.
              </p>
            </div>

            <div className="admin-tuning">
              <div className="admin-tuning-head">
                <h3 className="admin-tuning-title">ИИ-ассистенты</h3>
                <span className="admin-tuning-hint">
                  Боты в списке контактов: рисуют и обрабатывают картинки, пишут тексты. Ключи и
                  имена моделей — только в .env; здесь — то, что имеет смысл крутить на живом
                  боте. Доли и потолок ниже — наши, а не чьего-то бесплатного тарифа.
                </span>
              </div>

              <div className="admin-fields">
                {/* One readout per provider rather than one line of models:
                    with two bots a single «Модели: …» would be naming whichever
                    one happened to sort first. */}
                {ai.providers.map((provider) => (
                  <div key={provider.id} className="admin-field admin-field-readout">
                    <span>{provider.id === 'openai' ? 'OpenAI' : 'Gemini'}</span>
                    <strong>
                      {provider.configured
                        ? `@${provider.botUsername} · ${provider.textModel} + ${provider.imageModel}`
                        : 'не настроен'}
                    </strong>
                    <small>
                      {provider.configured
                        ? 'модели меняются только в .env и требуют перезапуска'
                        : `задайте ${provider.id === 'openai' ? 'OPENAI_API_KEY' : 'GEMINI_API_KEY'} в .env — пока этого бота в списке контактов нет`}
                    </small>
                  </div>
                ))}
                <div className="admin-field admin-field-readout">
                  <span>Потрачено за месяц</span>
                  <strong>
                    {formatMoney(ai.spend.monthMicros)} из {formatMoney(ai.spend.budgetMicros)}
                  </strong>
                  <small>
                    {ai.spend.byProvider.length === 0
                      ? 'пока ничего не потрачено'
                      : ai.spend.byProvider
                          .map(
                            (row) =>
                              `${row.provider}/${row.kind === 'image' ? 'картинки' : 'тексты'}: ${formatMoney(row.micros)}`,
                          )
                          .join(' · ')}
                  </small>
                </div>
                <div className="admin-field admin-field-readout">
                  <span>Потрачено сегодня</span>
                  <strong>
                    {ai.usedToday.image} из {ai.aiImageRpd} картинок, {ai.usedToday.text} из{' '}
                    {ai.aiTextRpd} текстовых
                  </strong>
                  <small>
                    по нашему счётчику, не по данным Google; сутки — по тихоокеанскому времени
                  </small>
                </div>
                <label className="admin-field">
                  <span>История, сообщений</span>
                  <input
                    type="number"
                    min={2}
                    max={100}
                    value={aiDraft.aiHistoryMessages}
                    onChange={(event) =>
                      setAiDraft({ ...aiDraft, aiHistoryMessages: event.target.value })
                    }
                  />
                  <small>из .env: {ai.envDefaults.aiHistoryMessages}</small>
                </label>
                <label className="admin-field">
                  <span>Лимит картинок в день</span>
                  <input
                    type="number"
                    min={1}
                    max={50000}
                    value={aiDraft.aiImageRpd}
                    onChange={(event) => setAiDraft({ ...aiDraft, aiImageRpd: event.target.value })}
                  />
                  <small>из .env: {ai.envDefaults.aiImageRpd}</small>
                </label>
                <label className="admin-field">
                  <span>Лимит текстовых в день</span>
                  <input
                    type="number"
                    min={1}
                    max={50000}
                    value={aiDraft.aiTextRpd}
                    onChange={(event) => setAiDraft({ ...aiDraft, aiTextRpd: event.target.value })}
                  />
                  <small>из .env: {ai.envDefaults.aiTextRpd}</small>
                </label>
                {/* The three below are ours rather than a mirror of somebody's
                    free tier, which is why they read as «на человека» and «за
                    месяц» — the two units an operator watching a bill thinks in. */}
                <label className="admin-field">
                  <span>Картинок на человека в день</span>
                  <input
                    type="number"
                    min={0}
                    max={200}
                    value={aiDraft.aiUserImagePerDay}
                    onChange={(event) =>
                      setAiDraft({ ...aiDraft, aiUserImagePerDay: event.target.value })
                    }
                  />
                  <small>из .env: {ai.envDefaults.aiUserImagePerDay}; 0 — картинки только вам</small>
                </label>
                <label className="admin-field">
                  <span>Ответов на человека в день</span>
                  <input
                    type="number"
                    min={1}
                    max={1000}
                    value={aiDraft.aiUserTextPerDay}
                    onChange={(event) =>
                      setAiDraft({ ...aiDraft, aiUserTextPerDay: event.target.value })
                    }
                  />
                  <small>из .env: {ai.envDefaults.aiUserTextPerDay}</small>
                </label>
                <label className="admin-field">
                  <span>Потолок за месяц, $</span>
                  <input
                    type="number"
                    min={1}
                    max={1000}
                    step={1}
                    value={aiDraft.aiMonthlyBudget}
                    onChange={(event) =>
                      setAiDraft({ ...aiDraft, aiMonthlyBudget: event.target.value })
                    }
                  />
                  <small>
                    из .env: ${(ai.envDefaults.aiMonthlyBudgetCents / 100).toFixed(2)}; исчерпан —
                    бот говорит об этом в чате
                  </small>
                </label>
                <label className="admin-field">
                  <span>Формат картинки по умолчанию</span>
                  <select
                    value={aiDraft.aiImageSize}
                    onChange={(event) =>
                      setAiDraft({ ...aiDraft, aiImageSize: event.target.value })
                    }
                  >
                    <option value="1024x1024">квадрат</option>
                    <option value="1024x1536">вертикаль</option>
                    <option value="1536x1024">горизонталь</option>
                  </select>
                  <small>когда композер не сказал; из .env: {ai.envDefaults.aiImageSize}</small>
                </label>
                <label className="admin-field">
                  <span>Качество по умолчанию</span>
                  <select
                    value={aiDraft.aiImageQuality}
                    onChange={(event) =>
                      setAiDraft({ ...aiDraft, aiImageQuality: event.target.value })
                    }
                  >
                    <option value="low">черновик</option>
                    <option value="medium">хорошее</option>
                    <option value="high">максимум</option>
                  </select>
                  <small>
                    из .env: {ai.envDefaults.aiImageQuality}; картинку просят два-три раза, прежде
                    чем она получится
                  </small>
                </label>
              </div>

              <label className="admin-field admin-field-wide">
                <span>Кто такой ассистент (системный промпт)</span>
                <textarea
                  className="admin-textarea"
                  rows={4}
                  value={aiDraft.systemPrompt}
                  onChange={(event) => setAiDraft({ ...aiDraft, systemPrompt: event.target.value })}
                />
                <small>
                  {ai.systemPromptOverridden
                    ? 'переопределён здесь; очистите поле и сохраните, чтобы вернуть из .env'
                    : 'значение из .env (AI_SYSTEM_PROMPT)'}
                </small>
              </label>

              <label className="admin-switch">
                <input
                  type="checkbox"
                  checked={aiDraft.aiReplies}
                  onChange={(event) => setAiDraft({ ...aiDraft, aiReplies: event.target.checked })}
                />
                <span>
                  Отвечать на сообщения
                  <span className="settings-hint">
                    Рубильник на случай странного поведения или съеденной квоты: бот остаётся в
                    контактах, но молчит. Из .env:{' '}
                    {ai.envDefaults.aiReplies ? 'включено' : 'выключено'}.
                  </span>
                </span>
              </label>

              <div className="admin-actions">
                <button
                  type="button"
                  className="primary-button admin-save"
                  disabled={busy}
                  onClick={() => void saveAiSettings()}
                >
                  Сохранить
                </button>
                <button
                  type="button"
                  className="settings-secondary"
                  disabled={busy || !aiTuned}
                  onClick={() => void resetAiSettings()}
                >
                  Вернуть значения из .env
                </button>
                {aiTuned && <Pill tone="warn">переопределено здесь, не из .env</Pill>}
              </div>

              <p className="admin-note">
                Бот отвечает только в личных сообщениях и работает на бесплатной квоте Gemini —
                лимиты выше это её зеркало, а не управление ею: когда Google меняет квоту,
                поправьте цифры здесь. Команда «лимит» в чате с ботом показывает те же числа.
                На бесплатном тарифе Google может использовать запросы (включая фото) для
                обучения — для клиентских снимков имеет смысл включить платный биллинг в
                Google AI Studio: код не меняется.
              </p>
            </div>
          </section>

          <section className="admin-section">
            <h2 className="admin-section-title">Уведомления</h2>
            <div className="admin-config">
              <div>
                <span className="admin-config-label">Push</span>
                {push.configured ? <Pill tone="good">включён</Pill> : <Pill tone="bad">выключен, нет ключей</Pill>}
              </div>
              <div>
                <span className="admin-config-label">Правило</span>
                <Pill tone="neutral">
                  уведомление на каждое сообщение, кроме открытого чата
                </Pill>
              </div>
            </div>

            <p className="admin-note">
              Настраивать здесь нечего, и это осознанно. Пауз, тихих часов и дайджеста больше нет:
              каждое из них умело проглотить сообщение, а снаружи задержанное уведомление
              неотличимо от не пришедшего. Молчит только тот чат, который у человека открыт на
              экране прямо сейчас — там сообщение и так видно. «Не беспокоить» на самом телефоне
              выбирает его владелец, и это единственная тишина, о которой просили.
            </p>

            <div className="admin-actions">
              <button
                type="button"
                className="settings-secondary"
                disabled={busy}
                onClick={() => void act(() => api.testPush(), 'Тестовое уведомление отправлено')}
              >
                Отправить себе тест
              </button>
              <button
                type="button"
                className="settings-secondary"
                disabled={busy}
                onClick={() =>
                  void act(
                    () => api.testPushDelayed(),
                    'Тест придёт через 30 секунд — заблокируйте телефон и положите его',
                  )
                }
              >
                Тест через 30 секунд
              </button>
              {notice && <span className="admin-notice">{notice}</span>}
            </div>

            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Пользователь</th>
                    <th>Сокет</th>
                    <th>На экране</th>
                    <th>Устройства</th>
                    <th>Доставлено (час)</th>
                    <th>Не ушло</th>
                    <th>Задержка</th>
                  </tr>
                </thead>
                <tbody>
                  {push.users.map((user) => {
                    const foreground = foregroundState(user);
                    return (
                      <tr key={user.username}>
                        <td>{user.username}</td>
                        <td>
                          {user.online ? (
                            <Pill tone="good">в сети</Pill>
                          ) : (
                            <Pill tone="neutral">офлайн</Pill>
                          )}
                        </td>
                        <td>
                          <Pill tone={foreground.tone}>{foreground.label}</Pill>
                        </td>
                        <td>
                          {user.subscriptions.length === 0 ? (
                            <Pill tone="bad">нет подписки</Pill>
                          ) : (
                            <span className="admin-hosts">
                              {user.subscriptions
                                .map(
                                  (sub) =>
                                    `${sub.host}${
                                      sub.lastAckSecondsAgo === null
                                        ? ' (нет квитанций)'
                                        : ` (${formatAge(sub.lastAckSecondsAgo)} назад)`
                                    }`,
                                )
                                .join(', ')}
                            </span>
                          )}
                        </td>
                        <td>
                          <Pill tone={deliveryTone(user.delivery)}>
                            {user.delivery.sent === 0
                              ? '—'
                              : `${user.delivery.acked} из ${user.delivery.sent}`}
                          </Pill>
                        </td>
                        {/* Refused by the push service, so it never got as far as a
                            device. The reason is spelled out beside the count —
                            without it, a run of these looks like the same problem
                            as a phone in Doze and is not. */}
                        <td>
                          <Pill tone={failureTone(user.delivery.failed)}>
                            {user.delivery.failed === 0 ? '—' : user.delivery.failed}
                          </Pill>
                          {user.delivery.lastError && (
                            <div className="admin-hosts">{user.delivery.lastError}</div>
                          )}
                        </td>
                        <td className="admin-hosts">
                          {user.delivery.medianLatency === null
                            ? '—'
                            : `${user.delivery.medianLatency} с · худшая ${user.delivery.worstLatency} с`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {/* The return leg of the loop the whole page is: tune here, live with it,
              then make it the default the next deployment starts from. */}
          <section className="admin-section">
            <h2 className="admin-section-title">Перенести в .env</h2>
            <div className="admin-tuning">
              <div className="admin-tuning-head">
                <h3 className="admin-tuning-title">Что сейчас переопределено</h3>
                <span className="admin-tuning-hint">
                  Всё, что настроено на этой странице, живёт в базе, а не в <code>.env</code> — и
                  переедет вместе с ней. Здесь эти значения собраны строками для <code>.env</code>,
                  уже в нужных единицах: секунды, минуты, байты. Вставьте их перед следующим
                  деплоем — и можно нажимать «вернуть значения из .env».
                </span>
              </div>

              <div className="admin-actions">
                <button
                  type="button"
                  className="settings-secondary"
                  disabled={busy}
                  onClick={() => void toggleEnv()}
                >
                  {envText ? 'Скрыть' : 'Показать строки для .env'}
                </button>
                {envText && envText.overrides.length > 0 && (
                  <button type="button" className="settings-secondary" onClick={() => void copyEnv()}>
                    {copied ? 'Скопировано' : 'Копировать'}
                  </button>
                )}
              </div>

              {envText &&
                (envText.overrides.length === 0 ? (
                  <p className="admin-note">
                    Ничего не переопределено — приложение работает ровно на том, что написано в{' '}
                    <code>.env</code>. Копировать нечего.
                  </p>
                ) : (
                  <>
                    <pre className="admin-env" ref={envBlock}>
                      {envText.text}
                    </pre>
                    <div className="admin-table-wrap">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>Переменная</th>
                            <th>Сейчас</th>
                            <th>В .env</th>
                          </tr>
                        </thead>
                        <tbody>
                          {envText.overrides.map((entry) => (
                            <tr key={entry.name}>
                              <td>
                                <code>{entry.name}</code>
                              </td>
                              <td>{entry.value}</td>
                              <td className="admin-hosts">{entry.envValue}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ))}
            </div>
          </section>

          <section className="admin-section">
            <h2 className="admin-section-title">Последние решения</h2>
            {push.recent.length === 0 ? (
              <p className="admin-note">Пока ничего — отправьте сообщение.</p>
            ) : (
              <ul className="admin-log">
                {push.recent.map((entry, index) => (
                  <li key={`${entry.at}-${index}`}>
                    <span className="admin-log-time">
                      {new Date(entry.at).toLocaleTimeString('ru-RU')}
                    </span>
                    <span className="admin-log-user">{entry.username}</span>
                    <Pill tone={outcomeTone(entry.outcome)}>
                      {OUTCOME_LABEL[entry.outcome] ?? entry.outcome}
                    </Pill>
                    <span className="admin-log-detail">{entry.detail}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}
